/**
 * Pure helpers for the PARENT→iframe construction-event bridge (design
 * `docs/design/live-preview-orchestration.md` §3.G).
 *
 * `preview:construction-event` is an Electron IPC push that reaches the
 * **Foundry renderer process** — but the `v1:preview` Ingot's bottom-strip
 * narration runs **inside the display `<iframe>`**, a separate browsing
 * context that does NOT receive the parent's Electron IPC. So the records
 * must cross the parent→iframe boundary. This is crucible/GUI's job (the
 * parent owns the iframe). The mechanism is `postMessage` from the parent
 * overlay into the display iframe.
 *
 * This module owns ONLY the PARENT POST side — the pure message builders +
 * the per-gameId post decision. The CHILD/consumer side (the
 * origin-validating `window.addEventListener('message', …)` listener that
 * checks `event.source === window.parent` AND `event.origin === the Foundry
 * parent origin`) is the `v1:preview` Ingot's job, NOT this PR.
 *
 * Extracted as pure functions (mirroring `auto-open-preview.ts` /
 * `preview-change-request.ts`) so the message shapes + the post decision are
 * unit-tested directly instead of by mounting the iframe machinery.
 *
 * CRITICAL (§3.G + the card gotcha): the parent sets `targetOrigin =
 * displayOrigin` (the display dev-server origin, derived from the preview
 * URL) — NEVER `'*'`. A `'*'` target would leak the records to any document
 * that happened to occupy the iframe. The child validates the PARENT's
 * origin (its own renderer origin), not the display origin — that is the
 * Ingot's listener, not this module.
 *
 * British spelling throughout (behaviour, colour, etc.).
 */

import type { ConstructionEvent } from '@volley/crucible-ipc-types/construction-event';

/** The bridge marker every parent→iframe message carries so the child
 *  listener can filter on `__cruciblePreview` (§3.G). */
export const CRUCIBLE_PREVIEW_BRIDGE_MARKER = '__cruciblePreview' as const;

/**
 * The construction-event bridge payload — one `ConstructionEvent` record
 * relayed verbatim into the display iframe (§3.G). The Ingot's narration
 * strip listens for `__cruciblePreview === 'construction-event'`.
 */
export interface ConstructionEventBridgeMessage {
  __cruciblePreview: 'construction-event';
  record: ConstructionEvent;
}

/**
 * The build-active banner payload — a DISTINCT bridge shape (§3.G codex
 * round-2 P2). The "rough cut / under construction" banner has NO
 * construction-log record and the iframe cannot receive `agent:stream`
 * directly, so it needs its own message shape rather than riding the
 * construction-event payload. Posted on build-turn start (`active: true`)
 * and on build-turn done/error (`active: false`).
 */
export interface BuildActiveBridgeMessage {
  __cruciblePreview: 'build-active';
  active: boolean;
  buildId: string;
}

export type PreviewBridgeMessage = ConstructionEventBridgeMessage | BuildActiveBridgeMessage;

/**
 * Build the construction-event bridge message for a single record (§3.G).
 * The record is relayed verbatim — the Ingot consumer imports the SAME
 * `ConstructionEvent` type, so the shape never drifts.
 */
export function buildConstructionEventMessage(
  record: ConstructionEvent,
): ConstructionEventBridgeMessage {
  return { __cruciblePreview: 'construction-event', record };
}

/**
 * Build the build-active banner message (§3.G / §5.4). `active` is the
 * build-turn state (true on start, false on done/error); `buildId` ties the
 * banner to the build that produced the records.
 */
export function buildBuildActiveMessage(
  active: boolean,
  buildId: string,
): BuildActiveBridgeMessage {
  return { __cruciblePreview: 'build-active', active, buildId };
}

/**
 * Derive the display-iframe ORIGIN from its preview URL — the `targetOrigin`
 * the parent posts to (§3.G). Returns `null` when the URL is missing or not
 * a parseable absolute URL (in which case the bridge MUST NOT post — never
 * fall back to `'*'`).
 */
export function deriveDisplayOrigin(displayUrl: string | undefined | null): string | null {
  if (!displayUrl) return null;
  try {
    return new URL(displayUrl).origin;
  } catch {
    return null;
  }
}

/**
 * If `record` is a session marker (`session-begin` / `session-attached`),
 * return its `buildId` — the canonical build identifier (§3.A). Used to
 * latch the current `buildId` for the build-active banner payload, since the
 * renderer has no other direct source for it. Returns `null` for every other
 * record kind.
 */
export function extractBuildId(record: ConstructionEvent): string | null {
  if (record.kind === 'session-begin' || record.kind === 'session-attached') {
    return record.buildId;
  }
  return null;
}

/** Inputs to the per-gameId post decision (§3.G + §3.E per-gameId guard). */
export interface PostDecisionInput {
  /** Whether the live-preview feature flag is ON. Flag-OFF → never post
   *  (the subscription is never made either; this is belt-and-braces). */
  enabled: boolean;
  /** The gameId the bridge SUBSCRIBED for — the game whose iframe this
   *  bridge instance owns. */
  boundGameId: string | null | undefined;
  /** The gameId currently active in the Workspace. When it diverges from
   *  `boundGameId` (a stale subscription mid-switch) we must NOT post — the
   *  per-gameId guard that stops game A's events reaching game B's iframe. */
  activeGameId: string | null | undefined;
  /** The display iframe's origin (`deriveDisplayOrigin`). `null` → no
   *  parseable origin → MUST NOT post (never `'*'`). */
  displayOrigin: string | null;
  /** The display URL the parent currently expects the iframe to host. */
  displayUrl?: string | null;
  /** The display URL whose iframe load event has completed. */
  loadedDisplayUrl?: string | null;
  /** Whether the display iframe currently has a `contentWindow` to post
   *  into (false before the iframe mounts / after it unmounts). */
  hasContentWindow: boolean;
}

/**
 * Decide whether the parent may post a bridge message into the display
 * iframe right now (§3.G). ALL must hold:
 *  - the feature flag is ON;
 *  - the bridge is bound to a concrete gameId that STILL matches the active
 *    game (the per-gameId guard — no cross-game leakage);
 *  - a parseable display origin exists (so `targetOrigin` is the display
 *    origin, never `'*'`);
 *  - the iframe has a `contentWindow` to receive the message.
 */
export function shouldPostToIframe(input: PostDecisionInput): boolean {
  if (!input.enabled) return false;
  if (!input.boundGameId) return false;
  // Per-gameId guard — never post game A's events into game B's iframe.
  if (input.boundGameId !== input.activeGameId) return false;
  if (input.displayOrigin === null) return false;
  if (!input.displayUrl) return false;
  if (input.loadedDisplayUrl !== input.displayUrl) return false;
  if (!input.hasContentWindow) return false;
  return true;
}
